// Files the broker sends WITH the RFP.
//
// Stored against the quote, so they survive saving and resuming a draft, and
// afterwards they are the record of what the carrier actually received.
//
// Not apiRequest: it forces Content-Type application/json and stringifies the
// body (queryClient.ts:67-73), which would mangle the file and strip the
// multipart boundary. Every FormData upload in this codebase calls fetch.

import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Paperclip, Trash2, Loader2, Upload, FileText, Download } from "lucide-react";
import {
  getPlanTypeAccent,
  getPlanTypeMeta,
  normalizePlanType,
  planTypeRank,
} from "@shared/schemas/planTypes";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type RfpAttachment = {
  id: number;
  fileName: string;
  fileSize: number;
  mimeType: string;
  /** Set when this is a plan's SBC rather than an uploaded file. */
  planDocumentId: number | null;
};

type PlanDocumentOption = {
  id: number;
  name: string;
  docType: string;
  planName: string | null;
  /** The plan's coverage line, so a dental summary is not read as the SBC. */
  coverageType: string | null;
  /** A link has no bytes, so it cannot be attached to an email. */
  externalUrl: string | null;
};

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * The file's kind, as a colour and three letters. Keyed on the extension, not
 * mimeType: a plan document's mimeType is often a generic octet-stream.
 */
const fileMark = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (extension === "pdf") {
    return { label: "PDF", tone: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" };
  }
  if (["xls", "xlsx", "csv"].includes(extension)) {
    return {
      label: extension === "csv" ? "CSV" : "XLS",
      tone: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    };
  }
  if (["doc", "docx"].includes(extension)) {
    return { label: "DOC", tone: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" };
  }
  return {
    label: extension.slice(0, 3).toUpperCase() || "FILE",
    tone: "bg-muted text-muted-foreground",
  };
};

export function RfpAttachments({
  quoteId,
  coverageTypes = [],
  currentMessage = "",
  onAddLink,
}: {
  quoteId: number;
  /**
   * The lines this RFP covers, so the list can say which have no summary on
   * file. Listing only what exists reads as "there was nothing to send" when it
   * may mean "nobody ever uploaded it".
   */
  coverageTypes?: string[];
  /** The shared custom message, so a link is not offered twice. */
  currentMessage?: string;
  onAddLink?: (name: string, url: string) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const linkInMessage = (url: string) => currentMessage.includes(url);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ["/api/rfps", quoteId, "attachments"];

  const { data: attachments = [] } = useQuery<RfpAttachment[]>({
    queryKey,
    queryFn: async () => {
      const res = await fetch(`/api/rfps/${quoteId}/attachments`, { credentials: "include" });
      if (!res.ok) throw new Error("Could not load attachments");
      return res.json();
    },
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      const res = await fetch(`/api/rfps/${quoteId}/attachments`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      // fetch does not throw on 4xx, and the server's refusals here are the
      // useful ones: too large, wrong type. Surface the message it sends.
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message ?? "Upload failed");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error: Error) =>
      toast({ title: "Could not attach that", description: error.message, variant: "destructive" }),
  });

  // Benefit summaries already on file for this client's current plans.
  const { data: planDocuments = [] } = useQuery<PlanDocumentOption[]>({
    queryKey: ["/api/rfps", quoteId, "plan-documents"],
    queryFn: async () => {
      const res = await fetch(`/api/rfps/${quoteId}/plan-documents`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const attachFromPlan = useMutation({
    mutationFn: async (planDocumentId: number) => {
      const res = await apiRequest("POST", `/api/rfps/${quoteId}/attachments/from-plan`, {
        planDocumentId,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message ?? "Could not attach that document");
      return body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error: Error) =>
      toast({ title: "Could not attach it", description: error.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/rfps/${quoteId}/attachments/${id}`);
      if (!res.ok) throw new Error("Could not remove the attachment");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error: Error) =>
      toast({ title: "Could not remove it", description: error.message, variant: "destructive" }),
  });

  // A link has no bytes to send, so it is never a candidate for attaching.
  const attachable = planDocuments.filter((doc) => !doc.externalUrl);
  // normalizePlanType answers "health" for null, so an untyped document would
  // claim to be the medical summary. Only a document that names its line counts
  // as one, here and in the ordering.
  const lineOf = (doc: PlanDocumentOption): string | null =>
    doc.coverageType ? normalizePlanType(doc.coverageType) : null;
  // Read in the same order as everything else in the RFP, untyped last.
  const sortedPlanDocuments = [...planDocuments].sort((a, b) => {
    const rank = (doc: PlanDocumentOption) =>
      lineOf(doc) ? planTypeRank(lineOf(doc)!) : Number.MAX_SAFE_INTEGER;
    return rank(a) - rank(b);
  });
  const linesOnFile = new Set(planDocuments.map(lineOf).filter(Boolean));
  const linesWithNothing = coverageTypes
    .map((type) => normalizePlanType(type))
    .filter((type) => !linesOnFile.has(type));
  // An already-attached document is in the list above as an attachment; listing
  // it twice would offer "Attach" for something already attached.
  const unattachedPlanDocuments = sortedPlanDocuments.filter(
    (doc) => !attachments.some((attachment) => attachment.planDocumentId === doc.id),
  );
  // A prompt, never an automatic attach: a file must not reach every carrier
  // without somebody choosing it.
  const unattached = attachments.length === 0 ? attachable : [];

  // The "Attached" row of the envelope block, so no separator, icon or label of
  // its own: the row supplies all three.
  return (
    <div className="space-y-1">
    <div className="flex items-center gap-2">
      <span className="min-w-0 flex-1 truncate text-sm">
        {attachments.length
          ? attachments
              .map((attachment) =>
                attachment.planDocumentId !== null
                  ? `${attachment.fileName} (from plan)`
                  : attachment.fileName,
              )
              .join(" · ")
          : "No attachments"}
      </span>
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            className="shrink-0 text-sm font-medium text-primary hover:underline"
          >
            {attachments.length ? "Manage attachments" : "Add attachments"}
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Attachments</DialogTitle>
            <DialogDescription>
              PDF, Word or Excel. Everyone on this RFP receives the same files.
            </DialogDescription>
          </DialogHeader>
        {/* min-w-0: DialogContent is a grid, and a grid child defaults to
            min-width:auto, so a long file name widens this past the dialog. */}
        <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {attachments.length === 0
                ? "Nothing attached yet"
                : attachments.length === 1
                  ? "1 file goes with this RFP"
                  : `${attachments.length} files go with this RFP`}
            </span>
          </div>
          {/* Hidden while empty: the dashed well below is the same action. */}
          {attachments.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {upload.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Add a file
            </Button>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            onChange={(event) => {
              // Copy the files out BEFORE clearing the input. mutate() is async,
              // and clearing an input's value empties its FileList, so clearing
              // first left the mutation with nothing to send.
              const picked = Array.from(event.target.files ?? []);
              // Cleared so picking the same file twice still fires onChange.
              event.target.value = "";
              if (picked.length) upload.mutate(picked);
            }}
          />
        </div>

        {attachments.length === 0 && unattachedPlanDocuments.length === 0 && (
          <button
            type="button"
            disabled={upload.isPending}
            onClick={() => fileInput.current?.click()}
            className="flex w-full flex-col items-center gap-1 rounded-md border border-dashed py-6 text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-foreground"
          >
            <Upload className="h-5 w-5" />
            {/* "click", not "drop": nothing here handles a drag event. */}
            <span className="text-sm font-medium">Click to pick a file</span>
            <span className="text-xs">PDF, Word or Excel</span>
          </button>
        )}

        {/* One list: what is going out, what is on file and not going out, and
            what does not exist at all. The same question, so the same list. */}
        {(attachments.length > 0 || unattachedPlanDocuments.length > 0) && (
          <div className="divide-y rounded-md border">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center gap-2.5 px-3 py-2 hover:bg-muted/40"
              >
                <span
                  className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[9px] font-bold ${
                    fileMark(attachment.fileName).tone
                  }`}
                >
                  {fileMark(attachment.fileName).label}
                </span>
                {/* Inline, so a PDF opens in the browser's own viewer. */}
                <span className="min-w-0 flex-1">
                  <a
                    href={`/api/rfps/${quoteId}/attachments/${attachment.id}/file`}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-sm font-medium hover:underline"
                    title={`Open ${attachment.fileName}`}
                  >
                    {attachment.fileName}
                  </a>
                  <span className="block text-[11px] text-muted-foreground">
                    {formatSize(attachment.fileSize)}
                    {/* Removing one of these only unlinks it: the plan keeps
                        its copy. */}
                    {attachment.planDocumentId !== null && (
                      <span className="ml-1.5 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-violet-700 dark:bg-violet-950 dark:text-violet-300">
                        From plan
                      </span>
                    )}
                  </span>
                </span>
                <a
                  href={`/api/rfps/${quoteId}/attachments/${attachment.id}/file?download=1`}
                  className="shrink-0 rounded p-1.5 text-muted-foreground hover:text-foreground"
                  title={`Download ${attachment.fileName}`}
                  aria-label={`Download ${attachment.fileName}`}
                >
                  <Download className="h-3.5 w-3.5" />
                </a>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(attachment.id)}
                  aria-label={`Remove ${attachment.fileName}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}

            {/* On the client's plans but not going out yet. */}
            {unattachedPlanDocuments.map((doc) => {
              const line = lineOf(doc);
              const accent = getPlanTypeAccent(line ?? "");
              const isLink = !!doc.externalUrl;
              return (
                <div key={`doc-${doc.id}`} className="flex items-center gap-2.5 px-3 py-2 hover:bg-muted/40">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium" title={doc.name}>
                      {line && (
                        <span
                          className={`mr-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${accent.accentBg} ${accent.accentText}`}
                        >
                          {getPlanTypeMeta(line).short}
                        </span>
                      )}
                      {doc.name}
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      On this client's plan{doc.planName ? ` · ${doc.planName}` : ""}
                    </span>
                  </span>
                  {isLink ? (
                    // No bytes to attach, so the next best thing is a clickable
                    // address in the email. The carrier still gets the document.
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 shrink-0 text-xs"
                      disabled={!onAddLink || linkInMessage(doc.externalUrl!)}
                      onClick={() => onAddLink?.(doc.name, doc.externalUrl!)}
                    >
                      {linkInMessage(doc.externalUrl!) ? "In message" : "Add link"}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 shrink-0 text-xs"
                      disabled={attachFromPlan.isPending}
                      onClick={() => attachFromPlan.mutate(doc.id)}
                    >
                      Attach
                    </Button>
                  )}
                </div>
              );
            })}

            {/* A row, not a footnote: this is the only line that says a
                document the carrier expects does not exist. */}
            {linesWithNothing.map((line) => {
              const accent = getPlanTypeAccent(line);
              return (
                <div key={`missing-${line}`} className="flex items-center gap-2.5 px-3 py-2">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-dashed">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">
                      <span
                        className={`mr-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${accent.accentBg} ${accent.accentText}`}
                      >
                        {getPlanTypeMeta(line).short}
                      </span>
                      <span className="text-muted-foreground">No summary on file</span>
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      Nothing saved on this client's {getPlanTypeMeta(line).label.toLowerCase()} plan
                    </span>
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 shrink-0 text-xs"
                    disabled={upload.isPending}
                    onClick={() => fileInput.current?.click()}
                  >
                    <Upload className="mr-1.5 h-3 w-3" />
                    Upload
                  </Button>
                </div>
              );
            })}
          </div>
        )}


        </div>
        </DialogContent>
      </Dialog>
    </div>

    {/* Ranked below the state line, not beside it: next to "Manage
        attachments" it read as a second copy of the same control. */}
    {unattached.length > 0 && (
      <p className="flex items-baseline gap-1.5 text-[11px] text-muted-foreground">
        <span className="min-w-0 truncate">
          {unattached.length === 1
            ? `${unattached[0].docType} on file for ${unattached[0].planName || unattached[0].name}`
            : `${unattached.length} documents on file for this client's plans`}
        </span>
        {unattached.length === 1 ? (
          <button
            type="button"
            disabled={attachFromPlan.isPending}
            onClick={() => attachFromPlan.mutate(unattached[0].id)}
            className="shrink-0 font-medium text-primary hover:underline disabled:opacity-50"
          >
            {attachFromPlan.isPending ? "Attaching..." : "Attach it"}
          </button>
        ) : (
          // More than one is a real choice, so it belongs in the dialog.
          <span className="shrink-0">none attached</span>
        )}
      </p>
    )}
    </div>
  );
}
